import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { withErrorReason } from "@components/CatalogIndex";
import { useV2ExportProjectsMutation } from "@generated";
import {
  buildProjectsListArgs,
  type ProjectsListParams,
} from "../api/useProjectsListQuery";
import { sortParam } from "../columns/columnRegistry";

export const EXPORT_STARTED_MESSAGE =
  "Your export has started. The CSV will be emailed to you shortly.";

// The list's own filter and sort, so the CSV holds what's on screen. Page is
// irrelevant: the export covers every page.
export const exportProjectsBody = (params: ProjectsListParams) => ({
  data: {
    attributes: {
      include_archived: params.tab === "archived",
      filter: buildProjectsListArgs(params).filter ?? {},
      sort: sortParam(params.sort),
    },
  },
});

interface Props {
  show: boolean;
  params: ProjectsListParams;
  onClose: () => void;
  onResult: (toast: { variant: "success" | "error"; text: string }) => void;
}

const ExportProjectsModal = ({ show, params, onClose, onResult }: Props) => {
  const [exportProjects, { isLoading }] = useV2ExportProjectsMutation();

  const handleExport = async () => {
    if (isLoading) return;
    try {
      await exportProjects({ body: exportProjectsBody(params) }).unwrap();
      onResult({ variant: "success", text: EXPORT_STARTED_MESSAGE });
    } catch (e) {
      onResult({
        variant: "error",
        text: withErrorReason("Couldn't start the export.", e),
      });
    }
    onClose();
  };

  return (
    <ScopeStackModal
      show={show}
      modalTitle="Export Projects to CSV"
      modalBody={
        <p className="instructionalText">
          The projects in this list, with its current tab, search, filters and
          sort, will be emailed to you as a CSV. Change requests are listed
          after their parent project.
        </p>
      }
      button1Text="Cancel"
      handleButton1Click={onClose}
      handleClose={onClose}
      button2Text={isLoading ? "Exporting..." : "Export"}
      button2Disabled={isLoading}
      handleButton2Click={() => void handleExport()}
    />
  );
};

export default ExportProjectsModal;
